import React, { useEffect, useState } from "react";
import {
  Container,
  Paper,
  Typography,
  Box,
  IconButton,
  Link,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  Button,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { QRCodeSVG } from "qrcode.react";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import LocationMap from "../Map/LocationMap";
import PDFViewer from "../PDF/PDFViewer";
import SlideshowWithModal from "../Slideshow/SlideshowWithModal";
import YouTubePlayer from "../Video/YouTubePlayer";
import { useAuth } from "../../contexts/JWTAuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { getUnits } from "../../Services/unitsService";
import brochurePDF from "../../Assets/pdf/Brochure New Ar 130325.pdf";
import UnitInfoCard from "./UnitInfoCard";
import { ImageBaseUrl } from "../../Services/config";
import unitsData from "./unitsnajma.json";

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  borderRadius: "12px !important",
  marginBottom: theme.spacing(2),
  boxShadow: "0 2px 8px rgba(70, 80, 100, 0.1)",
  border: "1px solid #e9ecef",
  overflow: "hidden",
  "&:before": {
    display: "none",
  },
  "&.Mui-expanded": {
    margin: theme.spacing(2, 0),
  },
}));

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  background: "#465064",
  color: "white",
  minHeight: "60px !important",
  padding: theme.spacing(0, 3),
  "& .MuiAccordionSummary-content": {
    margin: theme.spacing(1.5, 0),
  },
  "& .MuiAccordionSummary-expandIconWrapper": {
    color: "white",
    transform: "rotate(0deg)",
    transition: "transform 0.2s ease",
    "&.Mui-expanded": {
      transform: "rotate(180deg)",
    },
  },
  "&:hover": {
    background: "#3a4254",
  },
}));

const StyledAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
  padding: theme.spacing(3),
  background: "#f8f9fa",
}));

const LinkBox = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(1),
  padding: theme.spacing(2),
  background: "white",
  borderRadius: theme.spacing(1),
  marginTop: theme.spacing(2),
  width: "100%",
  maxWidth: "500px",
  boxShadow: "0 2px 8px rgba(70, 80, 100, 0.1)",
  border: "1px solid #e9ecef",
}));

const StyledLink = styled(Link)(({ theme }) => ({
  flex: 1,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  color: "#465064",
  textDecoration: "none",
  "&:hover": {
    textDecoration: "underline",
  },
}));

// Dynamically import all images from the brochure folder
function importAll(r) {
  return r.keys().map(r);
}
const brochureImages = importAll(
  require.context("../../Assets/brochure", false, /\.jpe?g$/)
);

const salesPlanImages = importAll(
  require.context("../../Assets/sales plan", false, /\.jpe?g$/)
);

const MediaNajma = ({
  imageMap = true,
  showQRCode = true,
  view = true,
  useSameTabForUnitPayment = false,
  salesId,
  sessionId,
  onLoadingChange,
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const currentDomain = window.location.origin;
  const [qrValue, setQRvalue] = useState("");
  const [expanded, setExpanded] = useState(showQRCode ? "qr" : "brochure"); // Set default based on showQRCode prop
  const [units, setUnits] = useState([]); // State to store all units
  const [loading, setLoading] = useState(false); // Loading state for units
  // const [location, setLocation] = useState({
  //   latitude: 30.04686121883895, // Default Cairo coordinates
  //   longitude: 31.52336850739469,
  //   title: "Our Location",
  // });
  const [pdfUrl, setPdfUrl] = useState(""); // Add your PDF URL here
  const [videoId, setVideoId] = useState("xy2Sj63KZ_s"); // Default YouTube video ID

  // Track readiness: units loaded + first image of each slideshow loaded in DOM
  const [unitsLoaded, setUnitsLoaded] = useState(false);
  const [salesPlanImgLoaded, setSalesPlanImgLoaded] = useState(salesPlanImages.length === 0);
  const [brochureImgLoaded, setBrochureImgLoaded] = useState(brochureImages.length === 0);

  // Handle accordion expansion
  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  // Handle QR code copy
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(qrValue);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  // Handle QR code open
  const handleOpen = () => {
    window.open(qrValue, "_blank", "noopener,noreferrer");
  };
  // Expose React Router navigate to inline onclick (avoids full page reload)
  useEffect(() => {
    const key = "__mediaNajmaNavigateToUnitPayment";
    window[key] = (unitId, viewParam) => {
      navigate(`/unit-payment/${unitId}/${viewParam}`, { state: { from: location.pathname } });
    };
    return () => {
      delete window[key];
    };
  }, [navigate]);

  useEffect(() => {
    fetchUnits();
  }, []);
  // Fetch all units from local JSON file
  const fetchUnits = async () => {
    try {
      setLoading(true);
      if (onLoadingChange) onLoadingChange(true);
      const response = await getUnits({ pageSize: 1000, pageIndex: 0 }); // Fetch all units
      console.log("Fetched units:", response);
      setUnits(response.Data || []);
    } catch (error) {
      console.error("Error fetching units:", error);
    } finally {
      setLoading(false);
      setUnitsLoaded(true);
    }
  };

  // Signal loading done only after units + first images are actually loaded in DOM
  useEffect(() => {
    if (!onLoadingChange) return;
    if (unitsLoaded && salesPlanImgLoaded && brochureImgLoaded) {
      onLoadingChange(false);
    }
  }, [unitsLoaded, salesPlanImgLoaded, brochureImgLoaded, onLoadingChange]);

  const createSlideShowImageMaps = (images) => {
    console.log(images);
    return ["", "0", "1", "2", "3"].map((image) => {
      const unit = units.filter((unit) => image.includes(unit.ImageMape));
      const value = unit.map((e) => {
        return {
          shape: "rect",
          alt: e.UnitNumber,
          description: e.UnitNumber,
          highlightColor:
            e.UnitStatus === 0
              ? "rgba(255, 255, 0, 0)"
              : "rgba(255, 0, 0, 0.25)",
          percentageCoords: e.LocationOnMap,
          content: e.UnitStatus === 0 && `
            <div style="padding: 20px; font-family: Arial, sans-serif;">
              <div style="background: #465064; color: white; padding: 15px; border-radius: 8px 8px 0 0; margin: -20px -20px 15px -20px;">
                <h3 style="margin: 0; font-size: 18px; font-weight: 600;">Unit ${
                  e.UnitNumber || "N/A"
                }</h3>
                <p style="margin: 5px 0 0 0; font-size: 14px; opacity: 0.8;">Building ${
                  e.BuildingNumber || "N/A"
                } - Floor ${e.Floor || "N/A"}</p>
              </div>
              
              <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-bottom: 15px;">
                <div style="background: #f8f9fa; padding: 10px; border-radius: 6px; border-left: 4px solid #465064;">
                  <div style="font-weight: bold; color: #495057; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px;">Area</div>
                  <div style="font-size: 16px; color: #212529; font-weight: 600;">${
                    e.Area || e.BUA || "N/A"
                  } m²</div>
                </div>
                
                <div style="background: #f8f9fa; padding: 10px; border-radius: 6px; border-left: 4px solid #28a745;">
                  <div style="font-weight: bold; color: #495057; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px;">BUA</div>
                  <div style="font-size: 16px; color: #212529; font-weight: 600;">${
                    e.BUA || "N/A"
                  } m²</div>
                </div>
              </div>
              
              <div style="background: #f8f9fa; padding: 15px; border-radius: 6px; margin-bottom: 15px;">
                <div style="font-weight: bold; color: #495057; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 8px;">Room Configuration</div>
                <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px;">
  
                  <div style="display: flex; align-items: center; gap: 5px;">
                    <span style="color: #667eea;">🚿</span>
                    <span style="font-size: 14px;">${
                      e.Bathrooms || "0"
                    } Bathrooms</span>
                    <span style="display: flex; align-items: center; gap: 5px;">
                    <span style="color: #667eea;">🚿</span>
                    <span style="font-size: 14px;">${
                      e.Bathrooms || "0"
                    } Bathrooms</span>
                  </span>
                  </div>
                </div>
              </div>
              
              ${
                e.UnitDescription
                  ? `
                <div style="background: #e3f2fd; padding: 12px; border-radius: 6px; border-left: 4px solid #2196f3; margin-bottom: 15px;">
                  <div style="font-weight: bold; color: #1976d2; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 5px;">Description</div>
                  <div style="font-size: 14px; color: #1565c0; line-height: 1.4;">${e.UnitDescription}</div>
                </div>
              `
                  : ""
              }
              
              ${
                e.UnitPrice
                  ? `
                <div style="background: linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%); padding: 15px; border-radius: 6px; text-align: center; margin-bottom: 15px;">
                  <div style="font-weight: bold; color: #d32f2f; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 5px;">Price</div>
                  <div style="font-size: 20px; color: #c62828; font-weight: bold;">$${e.UnitPrice.toLocaleString()}</div>
                </div>
              `
                  : ""
              }
              
              ${
                e.Files && e.Files.length > 0
                  ? `
                <div style="background: #f8f9fa; padding: 15px; border-radius: 6px; margin-bottom: 15px;">
                  <div style="font-weight: bold; color: #495057; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 15px;">Unit Documents (${
                    e.Files.length
                  })</div>
                  <div style="display: flex; flex-direction: column; gap: 15px;">
                    ${e.Files.map((rawFileName) => {
                      let fileName = rawFileName;
                      try { const p = JSON.parse(rawFileName); if (Array.isArray(p) && p.length > 0) fileName = p[0]; } catch (_) {}
                      const isPdf =
                        fileName.toLowerCase().includes(".pdf") ||
                        fileName.toLowerCase().includes("pdf");
                      if (isPdf) {
                        return `
                          <div style="position: relative; border-radius: 8px; overflow: hidden; box-shadow: 0 4px 12px rgba(0,0,0,0.15); background: white;">
                            <iframe 
                              src="${ImageBaseUrl}${fileName}" 
                              style="
                                width: 100%; 
                                height: 500px; 
                                border: none;
                                border-radius: 8px;
                                display: block;
                              "
                              title="Unit PDF Document"
                            ></iframe>
                            <div style="
                              position: absolute; 
                              top: 10px; 
                              right: 10px; 
                              background: rgba(0,0,0,0.7); 
                              color: white; 
                              padding: 4px 8px; 
                              border-radius: 12px; 
                              font-size: 11px;
                              pointer-events: none;
                            ">
                              📄 PDF Document
                            </div>
                            <div style="
                              position: absolute; 
                              bottom: 10px; 
                              right: 10px; 
                              background: #007bff; 
                              color: white; 
                              padding: 6px 12px; 
                              border-radius: 4px; 
                              font-size: 12px;
                              cursor: pointer;
                              text-decoration: none;
                              display: inline-block;
                            " onclick="window.open('${ImageBaseUrl}${fileName}', '_blank')">
                              Open PDF
                            </div>
                          </div>
                        `;
                      } else {
                        return `
                          <div style="position: relative; border-radius: 8px; overflow: hidden; box-shadow: 0 4px 12px rgba(0,0,0,0.15); background: white;">
                            <img 
                              src="${ImageBaseUrl}${fileName}" 
                              alt="Unit Image" 
                              style="
                                width: 100%; 
                                height: auto; 
                                max-height: 400px;
                                object-fit: contain; 
                                border-radius: 8px;
                                cursor: pointer;
                                transition: all 0.3s ease;
                                display: block;
                              "
                              onclick="window.open('${ImageBaseUrl}${fileName}', '_blank')"
                              onmouseover="this.style.transform='scale(1.02)'; this.style.boxShadow='0 8px 25px rgba(0,0,0,0.2)'"
                              onmouseout="this.style.transform='scale(1)'; this.style.boxShadow='0 4px 12px rgba(0,0,0,0.15)'"
                              onerror="this.style.display='none'"
                            />
                            <div style="
                              position: absolute; 
                              top: 10px; 
                              right: 10px; 
                              background: rgba(0,0,0,0.7); 
                              color: white; 
                              padding: 4px 8px; 
                              border-radius: 12px; 
                              font-size: 11px;
                              pointer-events: none;
                            ">
                              🔍 Click to enlarge
                            </div>
                          </div>
                        `;
                      }
                    }).join("")}
                  </div>
                </div>
              `
                  : ""
              }
              
              <div style="text-align: center; margin-bottom: 15px;">
                <button 
                  onclick="window.__mediaNajmaNavigateToUnitPayment && window.__mediaNajmaNavigateToUnitPayment('${e.Id}', '${view}')"
                  style="
                    background: #465064;
                    color: white;
                    border: none;
                    padding: 12px 24px;
                    border-radius: 6px;
                    font-size: 14px;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    box-shadow: 0 2px 4px rgba(70, 80, 100, 0.1);
                  "
                  onmouseover="this.style.background='#3a4254'; this.style.boxShadow='0 4px 8px rgba(70, 80, 100, 0.2)'"
                  onmouseout="this.style.background='#465064'; this.style.boxShadow='0 2px 4px rgba(70, 80, 100, 0.1)'"
                >
                  💳 Go to Payment
                </button>
              </div>
              
              <div style="margin-top: 15px; padding-top: 15px; border-top: 1px solid #dee2e6; font-size: 11px; color: #6c757d; text-align: center;">
                Unit ID: ${e.Id || "N/A"}
              </div>
            </div>
          `,
        };
      });
      return value;
    });
  };

  const slideshowImageMaps = createSlideShowImageMaps(salesPlanImages);
  console.log(slideshowImageMaps);
  

  useEffect(() => {
    if (showQRCode) {
      setExpanded((current) => (current ? current : "qr"));
    } else {
      setExpanded((current) =>
        current === "qr" ? "brochure" : current || "brochure"
      );
    }
  }, [showQRCode]);

  useEffect(() => {
    const resolvedSalesId = salesId ?? user?.UserId ?? user?.id ?? "";

    if (!resolvedSalesId) {
      setQRvalue("");
      return;
    }

    const params = new URLSearchParams();
    // params.set("salesId", resolvedSalesId);

    params.set("sessionId", sessionId);

    const queryString = params.toString();
    const qrUrl = `${currentDomain}/#/orientation/${resolvedSalesId}${
      queryString ? `?${queryString}` : ""
    }`;

    setQRvalue(qrUrl);
  }, [salesId, sessionId, user, currentDomain]);

  // Fetch units when component mounts
  useEffect(() => {
    fetchUnits();
  }, []);

  const getImgSrc = (img) =>
    typeof img === "string" ? img : img?.default || img;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        py: 2,
        px: { xs: 1, sm: 2, md: 3 },
      }}
    >
      {/* Hidden preload images — ensure first slide of each gallery is decoded before overlay closes */}
      <Box sx={{ display: "none", position: "absolute" }}>
        {salesPlanImages[0] && (
          <img
            src={getImgSrc(salesPlanImages[0])}
            onLoad={() => setSalesPlanImgLoaded(true)}
            onError={() => setSalesPlanImgLoaded(true)}
            alt=""
          />
        )}
        {brochureImages[0] && (
          <img
            src={getImgSrc(brochureImages[0])}
            onLoad={() => setBrochureImgLoaded(true)}
            onError={() => setBrochureImgLoaded(true)}
            alt=""
          />
        )}
      </Box>
      <Container
        maxWidth="lg"
        sx={{
          background: "white",
          borderRadius: 2,
          boxShadow: "0 2px 8px rgba(70, 80, 100, 0.1)",
          overflow: "hidden",
          p: { xs: 2, md: 3 },
        }}
      >
        <Grid container spacing={3}>
          {/* QR Code Section - Only show if showQRCode prop is true */}
          {showQRCode ? (
            <Grid item size={{ xs: 12, md: 12 }}>
              <StyledAccordion
                expanded={expanded === "qr"}
                onChange={handleAccordionChange("qr")}
              >
                <StyledAccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="qr-content"
                  id="qr-header"
                >
                  <Typography
                    variant="h5"
                    component="h2"
                    sx={{
                      fontWeight: "bold",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    📱 QR Code
                  </Typography>
                </StyledAccordionSummary>
                <StyledAccordionDetails>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    <Card
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        background: "white",
                        boxShadow: "0 2px 8px rgba(70, 80, 100, 0.1)",
                        mb: 2,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          my: 2,
                        }}
                      >
                        <QRCodeSVG
                          value={qrValue}
                          size={256}
                          level="H"
                          includeMargin={true}
                          imageSettings={{
                            src: "",
                            height: 24,
                            width: 24,
                            excavate: true,
                          }}
                        />
                      </Box>
                    </Card>
                    <LinkBox>
                      <Typography
                        variant="body2"
                        sx={{
                          flex: 1,
                          fontFamily: "monospace",
                          color: "#495057",
                        }}
                      >
                        {qrValue}
                      </Typography>
                      <IconButton
                        onClick={handleCopy}
                        color="primary"
                        size="small"
                        sx={{
                          width: "32px",
                          height: "32px",
                          "& .MuiSvgIcon-root": {
                            width: "20px",
                            height: "20px",
                          },
                          "&:hover": {
                            backgroundColor: "rgba(70, 80, 100, 0.1)",
                          },
                        }}
                      >
                        <ContentCopyIcon />
                      </IconButton>
                      <IconButton
                        onClick={handleOpen}
                        color="primary"
                        size="small"
                        sx={{
                          width: "32px",
                          height: "32px",
                          "& .MuiSvgIcon-root": {
                            width: "20px",
                            height: "20px",
                          },
                          "&:hover": {
                            backgroundColor: "rgba(70, 80, 100, 0.1)",
                          },
                        }}
                      >
                        <OpenInNewIcon />
                      </IconButton>
                    </LinkBox>
                  </Box>
                </StyledAccordionDetails>
              </StyledAccordion>
            </Grid>
          ) : (
            <Grid item size={{ xs: 12, md: 12 }}>
              <Typography
                variant="h5"
                component="h2"
                sx={{
                  fontWeight: "bold",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                No active session
              </Typography>
            </Grid>
          )}

          {/* Brochure Section */}
          <Grid item size={{ xs: 12, md: 12 }}>
            <StyledAccordion
              expanded={expanded === "brochure"}
              onChange={handleAccordionChange("brochure")}
            >
              <StyledAccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="brochure-content"
                id="brochure-header"
              >
                <Typography
                  variant="h5"
                  component="h2"
                  sx={{
                    fontWeight: "bold",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  📋 Brochure Gallery
                </Typography>
              </StyledAccordionSummary>
              <StyledAccordionDetails>
                <Card
                  sx={{
                    borderRadius: 2,
                    overflow: "hidden",
                    boxShadow: "0 2px 8px rgba(70, 80, 100, 0.1)",
                    mb: 2,
                  }}
                >
                  <SlideshowWithModal
                    images={brochureImages}
                    interval={5000}
                    autoPlay={false}
                    width="100%"
                    height="100%"
                    usePercentageCoords={true}
                  />
                </Card>
                <Box sx={{ textAlign: "center" }}>
                  <Button
                    href={brochurePDF}
                    download="Clinic-Brochure.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="contained"
                    sx={{
                      px: 4,
                      py: 1.5,
                      borderRadius: 2,
                      background: "#465064",
                      boxShadow: "0 2px 8px rgba(70, 80, 100, 0.2)",
                      "&:hover": {
                        background: "#3a4254",
                        boxShadow: "0 4px 12px rgba(70, 80, 100, 0.3)",
                      },
                      transition: "all 0.2s ease",
                      fontWeight: 500,
                    }}
                  >
                    📥 Download Brochure (PDF)
                  </Button>
                </Box>
              </StyledAccordionDetails>
            </StyledAccordion>
          </Grid>

          {/* Sales Plan Section */}
          <Grid item size={{ xs: 12, md: 12 }}>
            <StyledAccordion
              expanded={expanded === "sales-plan"}
              onChange={handleAccordionChange("sales-plan")}
            >
              <StyledAccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="sales-plan-content"
                id="sales-plan-header"
              >
                <Typography
                  variant="h5"
                  component="h2"
                  sx={{
                    fontWeight: "bold",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  🏢 Sales Plan & Unit Layouts
                </Typography>
              </StyledAccordionSummary>
              <StyledAccordionDetails>
                <Card
                  sx={{
                    borderRadius: 2,
                    overflow: "hidden",
                    boxShadow: "0 2px 8px rgba(70, 80, 100, 0.1)",
                  }}
                >
                  <SlideshowWithModal
                    images={salesPlanImages}
                    interval={5000}
                    width="100%"
                    height="100%"
                    autoPlay={false}
                    imageMaps={imageMap ? slideshowImageMaps : []}
                    usePercentageCoords={true}
                  />
                </Card>
              </StyledAccordionDetails>
            </StyledAccordion>
          </Grid>

          {/* Video Section */}
          <Grid item size={{ xs: 12, md: 12 }}>
            <StyledAccordion
              expanded={expanded === "video"}
              onChange={handleAccordionChange("video")}
            >
              <StyledAccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="video-content"
                id="video-header"
              >
                <Typography
                  variant="h5"
                  component="h2"
                  sx={{
                    fontWeight: "bold",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  🎥 Promotional Video
                </Typography>
              </StyledAccordionSummary>
              <StyledAccordionDetails>
                <Card
                  sx={{
                    borderRadius: 2,
                    overflow: "hidden",
                    boxShadow: "0 2px 8px rgba(70, 80, 100, 0.1)",
                  }}
                >
                  <YouTubePlayer videoId={videoId} />
                </Card>
              </StyledAccordionDetails>
            </StyledAccordion>
          </Grid>

          {/* Location Map Section */}
          <Grid item size={{ xs: 12, md: 12 }}>
            <StyledAccordion
              expanded={expanded === "location"}
              onChange={handleAccordionChange("location")}
            >
              <StyledAccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="location-content"
                id="location-header"
              >
                <Typography
                  variant="h5"
                  component="h2"
                  sx={{
                    fontWeight: "bold",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  📍 Our Location
                </Typography>
              </StyledAccordionSummary>
              <StyledAccordionDetails>
                <Card
                  sx={{
                    borderRadius: 2,
                    overflow: "hidden",
                    boxShadow: "0 2px 8px rgba(70, 80, 100, 0.1)",
                  }}
                >
                  <LocationMap
                    latitude={location.latitude}
                    longitude={location.longitude}
                    title={location.title}
                  />
                </Card>
              </StyledAccordionDetails>
            </StyledAccordion>
          </Grid>

          {/* PDF Viewer Section */}
          {pdfUrl && (
            <Grid item xs={12}>
              <StyledAccordion
                expanded={expanded === "pdf"}
                onChange={handleAccordionChange("pdf")}
              >
                <StyledAccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="pdf-content"
                  id="pdf-header"
                >
                  <Typography
                    variant="h5"
                    component="h2"
                    sx={{
                      fontWeight: "bold",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    📄 Document Viewer
                  </Typography>
                </StyledAccordionSummary>
                <StyledAccordionDetails>
                  <Card
                    sx={{
                      borderRadius: 3,
                      overflow: "hidden",
                      boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
                    }}
                  >
                    <PDFViewer pdfUrl={pdfUrl} />
                  </Card>
                </StyledAccordionDetails>
              </StyledAccordion>
            </Grid>
          )}
        </Grid>
      </Container>
    </Box>
  );
};

export default MediaNajma;
